import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Edit,
  Trash2,
  Tag,
  Calendar,
  Monitor,
  Printer,
  Package,
} from "lucide-react"
import type { Category } from "@/types"
import { formatDateHuman, cn } from "@/lib/utils"

/** First column (category name + icon) stays left-aligned. */
function categoriesPrimaryColLeftAligned(columnId: string) {
  return columnId === "name"
}

interface AdminCategoriesTableProps {
  data: Category[]
  onEdit: (category: Category) => void
  onDelete: (category: Category) => void
  isLoading?: boolean
}

export function AdminCategoriesTable({
  data,
  onEdit,
  onDelete,
  isLoading = false
}: AdminCategoriesTableProps) {
  const [sorting, setSorting] = useState<SortingState>([])

  const columns: ColumnDef<Category>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => {
        const isSorted = column.getIsSorted()
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2 lg:px-3"
          >
            <Tag className="mr-2 h-4 w-4" />
            Category
            {isSorted === "asc" ? (
              <ArrowUp className="ml-2 h-4 w-4" />
            ) : isSorted === "desc" ? (
              <ArrowDown className="ml-2 h-4 w-4" />
            ) : (
              <ArrowUpDown className="ml-2 h-4 w-4" />
            )}
          </Button>
        )
      },
      cell: ({ row }) => {
        const category = row.original
        return (
          <div className="flex items-center justify-start gap-3">
            <div className="flex-shrink-0">
              <div 
                className="h-8 w-8 rounded-lg flex items-center justify-center"
                style={{ 
                  backgroundColor: category.color || '#6B7280',
                  color: 'white'
                }}
              >
                <Tag className="h-4 w-4" />
              </div>
            </div>
            <div>
              <div className="font-medium text-foreground">
                {category.name}
              </div>
              <div className="text-sm text-muted-foreground line-clamp-1">
                {category.description || "No description"}
              </div>
            </div>
          </div>
        )
      },
    },
    {
      accessorKey: "product_count",
      header: ({ column }) => {
        const isSorted = column.getIsSorted()
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="mx-auto h-8 px-2 lg:px-3"
          >
            <Package className="mr-2 h-4 w-4" />
            Items
            {isSorted === "asc" ? (
              <ArrowUp className="ml-2 h-4 w-4" />
            ) : isSorted === "desc" ? (
              <ArrowDown className="ml-2 h-4 w-4" />
            ) : (
              <ArrowUpDown className="ml-2 h-4 w-4" />
            )}
          </Button>
        )
      },
      cell: ({ row }) => {
        const n = row.original.product_count ?? 0
        return (
          <div className="flex justify-center tabular-nums text-foreground font-medium">
            {n}
          </div>
        )
      },
    },
    {
      id: 'default_pct',
      header: () => (
        <span className="flex w-full justify-center text-sm font-medium">Default HS</span>
      ),
      cell: ({ row }) => {
        const v = row.original.default_pct_code?.trim()
        return (
          <div className="flex justify-center">
            <span className="font-mono text-xs text-foreground">{v || '—'}</span>
          </div>
        )
      },
    },
    {
      id: "kitchen_station",
      header: () => (
        <span className="flex w-full justify-center text-sm font-medium">Kitchen station</span>
      ),
      cell: ({ row }) => {
        const c = row.original
        const name = c.kitchen_station_name
        const out = c.kitchen_station_output_type
        if (!name) {
          return (
            <div className="flex justify-center">
              <span className="text-sm text-muted-foreground">—</span>
            </div>
          )
        }
        return (
          <div className="flex flex-col items-center justify-center gap-1">
            <span className="text-sm text-foreground">{name}</span>
            <Badge variant="outline" className="w-fit gap-1 text-xs font-normal">
              {out === "printer" ? (
                <>
                  <Printer className="h-3 w-3" /> Printer
                </>
              ) : (
                <>
                  <Monitor className="h-3 w-3" /> KDS
                </>
              )}
            </Badge>
          </div>
        )
      },
    },
    {
      accessorKey: "is_active",
      header: () => (
        <span className="flex w-full justify-center text-sm font-medium">Status</span>
      ),
      cell: ({ getValue }) => {
        const isActive = getValue() as boolean
        return (
          <div className="flex justify-center">
            <Badge variant={isActive ? "default" : "secondary"}>
              <div className={`w-2 h-2 rounded-full mr-2 ${isActive ? 'bg-success-solid' : 'bg-muted-foreground'}`} />
              {isActive ? "Active" : "Inactive"}
            </Badge>
          </div>
        )
      },
    },
    {
      accessorKey: "created_at",
      header: ({ column }) => {
        const isSorted = column.getIsSorted()
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="mx-auto h-8 px-2 lg:px-3"
          >
            <Calendar className="mr-2 h-4 w-4" />
            Created
            {isSorted === "asc" ? (
              <ArrowUp className="ml-2 h-4 w-4" />
            ) : isSorted === "desc" ? (
              <ArrowDown className="ml-2 h-4 w-4" />
            ) : (
              <ArrowUpDown className="ml-2 h-4 w-4" />
            )}
          </Button>
        )
      },
      cell: ({ getValue }) => {
        const date = getValue() as string
        return (
          <div className="flex justify-center text-foreground tabular-nums">
            {formatDateHuman(date)}
          </div>
        )
      },
    },
    {
      id: "actions",
      header: () => (
        <span className="flex w-full justify-center text-sm font-medium">Actions</span>
      ),
      cell: ({ row }) => {
        const category = row.original
        return (
          <div className="flex items-center justify-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onEdit(category)}
              className="h-8 px-2 lg:px-3"
            >
              <Edit className="h-4 w-4" />
              <span className="sr-only lg:not-sr-only lg:ml-2">Edit</span>
            </Button>
            {/* The "HS → items" bulk-apply button was removed 2026-08-03. It
                flattened every product in the category onto the category
                default with no preview or confirmation, which would have
                silently reverted five of NinetyPlus's eight corrected codes on
                a live FBR filer. See routes.go for the full rationale. */}
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDelete(category)}
              className="h-8 px-2 lg:px-3 text-red-600 hover:text-danger-fg hover:border-red-300"
            >
              <Trash2 className="h-4 w-4" />
              <span className="sr-only lg:not-sr-only lg:ml-2">Delete</span>
            </Button>
          </div>
        )
      },
    },
  ]

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  })

  return (
    <div className="w-full min-w-0">
      <div className="overflow-x-auto rounded-md border bg-card [-webkit-overflow-scrolling:touch]">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const cid = header.column.id
                  return (
                    <TableHead
                      key={header.id}
                      className={cn(
                        "px-4 align-middle",
                        categoriesPrimaryColLeftAligned(cid) ? "text-left" : "text-center",
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                  {columns.map((col, j) => {
                    const cid = String(
                      col.id ?? ("accessorKey" in col ? (col as { accessorKey?: string }).accessorKey : "") ?? "",
                    )
                    const left = categoriesPrimaryColLeftAligned(cid)
                    return (
                      <TableCell key={j} className={cn("align-middle", left ? "text-left" : "text-center")}>
                        <div
                          className={cn(
                            "h-4 rounded bg-muted animate-pulse",
                            left ? "max-w-[90%]" : "mx-auto max-w-[80%]",
                          )}
                        />
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className="hover:bg-muted/50"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        "align-middle",
                        categoriesPrimaryColLeftAligned(cell.column.id) ? "text-left" : "text-center",
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <div className="flex flex-col items-center justify-center space-y-2">
                    <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
                      <Tag className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <p className="text-muted-foreground">No categories found</p>
                    <p className="text-sm text-muted-foreground">Create your first category to get started</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
